<template>
    <v-container fluid class="news-page">
        <!-- Programme of the active phase for every team, full width -->
        <section v-if="scheduleMatches.length" class="news-schedule">
            <team-matches-overview
                :matches="scheduleMatches"
                :title="scheduleTitle"
                @show-all="$router.push({ name: 'Schedule' })"
            />
        </section>

        <v-row v-if="!articles.length" justify="center">
            <v-col cols="12" class="cube-wrapper">
                <v-skeleton-loader type="image" class="cube-skeleton rounded-lg" />
            </v-col>
        </v-row>

        <template v-else>
            <v-row justify="center" class="my-0">
                <v-col cols="12" class="cube-wrapper py-0">
                    <div class="cube" :class="{ 'cube--on': cubeOn }">
                        <div class="cube-cables">
                            <span />
                            <span />
                        </div>

                        <div class="cube-ring cube-ring--top custom-orange">
                            <div class="led-ticker">
                                <div class="led-ticker__track">
                                    <template v-for="copy in 2">
                                        <span v-for="(article, index) in tickerArticles" :key="`${copy}-${index}`" class="led-ticker__item">
                                            <img class="led-ticker__logo" :src="require('@/assets/main_logo.png')">
                                            <span class="led-text">{{ article.title }}</span>
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <div class="cube-screen clickable" @click="goToArticle(articles[0])">
                            <div class="media-blur-backdrop" :style="{ backgroundImage: `url('${getArticleImage(articles[0])}')` }" />
                            <v-img
                                :src="getArticleImage(articles[0])"
                                contain
                                aspect-ratio="1.7778"
                                class="cube-screen__img"
                            >
                                <div class="cube-screen__shade" />
                                <div class="lower-third">
                                    <div class="lower-third__tag custom-orange">
                                        <span>ΤΕΛΕΥΤΑΙΑ ΝΕΑ</span>
                                        <span v-if="articles[0].date" class="lower-third__date">{{ formatDate(articles[0].date) }}</span>
                                    </div>
                                    <h1 class="lower-third__title">{{ articles[0].title }}</h1>
                                </div>
                            </v-img>
                            <div class="led-overlay" />
                        </div>

                        <div class="cube-ring cube-ring--bottom">
                            <div class="cube-ring__bottom-content">
                                <img class="led-ticker__logo" :src="require('@/assets/main_logo.png')">
                                <span class="led-text custom-orange-text">ΝΕΑ</span>
                                <span class="led-dot" />
                                <span class="led-text">{{ todayLabel }}</span>
                            </div>
                            <div class="led-overlay" />
                        </div>
                    </div>
                </v-col>
            </v-row>

            <v-row class="articles-grid">
                <v-col v-for="(article, index) in articles.slice(1)" :key="article.id || index" cols="12" sm="6" lg="4">
                    <article-card :article="article" />
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import ArticleCard from '@/components/ArticleCard'
    import TeamMatchesOverview from '@/components/TeamMatchesOverview'
    import articleService from '@/services/article'
    import seasonService from '@/services/season'
    import matchService from '@/services/match'
    import { pollWhileVisible, SCHEDULE_REFRESH_MS } from '@/utils/matchLive'

    const PHASE_LABELS = {
        placements: 'Προκριματικά',
        regular_season: 'Regular Season',
        playoffs: 'Playoffs',
        cup: 'Κύπελλο'
    }

    export default {
        components: { ArticleCard, TeamMatchesOverview },
        data() {
            return {
                page: 1,
                size: 7,
                totalPages: 0,
                articles: [],
                cubeOn: false,
                loading: false,
                scheduleMatches: [],
                schedulePhase: null,
                stopScheduleRefresh: null
            }
        },
        computed: {
            scheduleTitle() {
                const phase = PHASE_LABELS[this.schedulePhase]
                return phase ? `Πρόγραμμα · ${phase}` : 'Πρόγραμμα'
            },
            tickerArticles() {
                return this.articles.slice(0, this.size)
            },
            todayLabel() {
                return momentTimezone().tz('Europe/Athens').locale('el').format('dddd DD/MM').toUpperCase()
            }
        },
        created() {
            this.fetchArticles()
            this.fetchSchedule()
            this.stopScheduleRefresh = pollWhileVisible(() => this.fetchSchedule(true), SCHEDULE_REFRESH_MS)
        },
        mounted() {
            window.addEventListener('scroll', this.onScroll, { passive: true })
        },
        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll)
            this.stopScheduleRefresh()
        },
        methods: {
            goToArticle(article) {
                this.$router.push({ name: 'Article', params: { id: article.id } })
            },
            getArticleImage(article) {
                return `${'https://storage.googleapis.com'}/${article.image}`
            },
            formatDate(date) {
                return momentTimezone(date).tz('Europe/Athens').format('DD/MM/YYYY')
            },
            // Matches of the active phase (from the config) in the season running today.
            // silent: a background reload, which keeps the list on screen if it fails
            fetchSchedule(silent = false) {
                Promise.all([
                    this.$store.dispatch('ensureConfig').catch(() => this.$store.getters.getConfig),
                    seasonService.getSeasons(1, 100)
                ]).then(([config, seasonsResp]) => {
                    const phase = (config && config.activePhase) || 'regular_season'
                    const now = momentTimezone()
                    const season = (seasonsResp.data.content || []).find(item => now.isBetween(momentTimezone(item.startDate), momentTimezone(item.endDate)))
                    if (!season) {
                        return
                    }
                    const query = phase === 'cup'
                        ? `{"isCup":true,"season":"${season.id}"}`
                        : `{"phase":"${phase}","season":"${season.id}"}`
                    return matchService.getMatches(1, 300, { query }).then(resp => {
                        this.schedulePhase = phase
                        this.scheduleMatches = (resp.data.content || []).filter(match => (phase === 'cup' ? match.cup : !match.cup))
                    })
                }).catch(() => {
                    if (!silent) {
                        this.scheduleMatches = []
                    }
                })
            },
            onScroll() {
                const bottomOfWindow = window.innerHeight + window.scrollY >= document.documentElement.offsetHeight - 200
                if (bottomOfWindow && !this.loading && this.page < this.totalPages) {
                    this.page += 1
                    this.fetchArticles()
                }
            },
            fetchArticles() {
                this.loading = true
                articleService.getArticles(this.page, this.size).then(resp => {
                    this.articles.push(...resp.data.content)
                    this.totalPages = resp.data.totalPages
                    if (!this.cubeOn) {
                        setTimeout(() => { this.cubeOn = true }, 50)
                    }
                }).finally(() => {
                    this.loading = false
                })
            }
        }
    }
</script>

<style scoped>
/* The cables hang in the gap above the cube, so the cube itself starts where the other pages start */
.news-page {
    margin-top: -40px;

    --cube-red: 239, 68, 68;
    --cube-housing: #0d0d0f;
}

.cube-wrapper {
    display: flex;
    justify-content: center;
}

.cube-skeleton {
    width: 100%;
    max-width: 1100px;
    aspect-ratio: 16 / 9;
}

/* ---------- Programme bar (full width) ---------- */

/* sits almost on the navbar: 8px below it, instead of the usual page gap */
.news-schedule {
    margin: -20px 4px 0;
}

/* ---------- Cube ---------- */
.cube {
    position: relative;
    width: 100%;
    max-width: 1100px;
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    filter: drop-shadow(0 30px 40px rgba(var(--cube-red), 0.35));
}

.cube-cables {
    position: absolute;
    top: -40px;
    left: 0;
    right: 0;
    height: 40px;
    pointer-events: none;
}

.cube-cables span {
    position: absolute;
    top: 0;
    width: 2px;
    height: 100%;
    background: linear-gradient(to bottom, rgba(120, 120, 120, 0), #555);
}

.cube-cables span:first-child {
    left: 18%;
}

.cube-cables span:last-child {
    right: 18%;
}

.cube-ring {
    position: relative;
    width: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
}

.cube-ring--top {
    height: 54px;
    border-radius: 10px 10px 0 0;
    border: 3px solid var(--cube-housing);
    border-bottom: none;
}

.cube-ring--bottom {
    width: 100%;
    height: 48px;
    justify-content: center;
    background: linear-gradient(to bottom, #1a1a1d, var(--cube-housing));
    border-radius: 0 0 6px 6px;
    clip-path: polygon(0 0, 100% 0, 94% 100%, 6% 100%);
}

.cube-ring__bottom-content {
    display: flex;
    align-items: center;
    gap: 14px;
}

.cube-screen {
    position: relative;
    width: 100%;
    background: #000;
    border-left: 10px solid var(--cube-housing);
    border-right: 10px solid var(--cube-housing);
    border-top: 6px solid var(--cube-housing);
    border-bottom: 6px solid var(--cube-housing);
    overflow: hidden;
    transition: filter 0.25s ease;
}

.cube-screen__img {
    max-height: 560px;
}

.cube-screen:hover .cube-screen__img {
    filter: brightness(1.12);
}

.cube-screen__shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.75) 100%);
}

/* LED pixel grid + scanlines */
.led-overlay {
    position: absolute;
    z-index: 2;
    inset: 0;
    pointer-events: none;
    background-image:
        radial-gradient(rgba(0, 0, 0, 0.55) 35%, transparent 36%),
        repeating-linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 0, rgba(255, 255, 255, 0.03) 1px, transparent 1px, transparent 3px);
    background-size: 3px 3px, 100% 3px;
    opacity: 0.35;
}

.cube-ring--top::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: radial-gradient(rgba(0, 0, 0, 0.6) 35%, transparent 36%);
    background-size: 3px 3px;
    opacity: 0.5;
}

/* ---------- Lower third ---------- */
.lower-third {
    position: absolute;
    z-index: 1;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0 28px 24px;
    text-align: left;
}

.lower-third__tag {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 4px 12px;
    color: white;
    font-weight: 800;
    font-size: 0.8rem;
    letter-spacing: 0.12em;
    clip-path: polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
    padding-right: 22px;
}

.lower-third__date {
    font-weight: 500;
    opacity: 0.9;
}

.lower-third__title {
    margin-top: 8px;
    padding: 8px 14px;
    border-left: 4px solid rgb(var(--cube-red));
    background: rgba(0, 0, 0, 0.55);
    color: white;
    font-size: 1.9rem;
    line-height: 1.25;
    font-weight: 700;
    word-break: break-word;
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    overflow: hidden;
}

/* ---------- LED ticker ---------- */
.led-ticker {
    width: 100%;
    overflow: hidden;
}

.led-ticker__track {
    display: flex;
    width: max-content;
    animation: led-scroll 90s linear infinite;
}

.cube-ring--top:hover .led-ticker__track {
    animation-play-state: paused;
}

.led-ticker__item {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    padding-right: 48px;
    white-space: nowrap;
}

.led-ticker__logo {
    height: 34px;
    width: auto;
    object-fit: contain;
}

.led-text {
    color: white;
    font-weight: 700;
    font-size: 1.05rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    text-shadow: 0 0 6px rgba(255, 255, 255, 0.6), 0 0 14px rgba(255, 180, 120, 0.5);
}

.led-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgb(var(--cube-red));
    box-shadow: 0 0 8px rgb(var(--cube-red));
    animation: led-blink 1.4s ease-in-out infinite;
}

@keyframes led-scroll {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(-50%);
    }
}

@keyframes led-blink {
    50% {
        opacity: 0.2;
    }
}

/* ---------- Power-on ---------- */
.cube-screen,
.cube-ring {
    opacity: 0;
}

.cube--on .cube-screen,
.cube--on .cube-ring {
    animation: power-on 0.6s ease-out forwards;
}

.cube--on .cube-ring--bottom {
    animation-delay: 0.1s;
}

@keyframes power-on {
    0% {
        opacity: 0;
        filter: brightness(3);
    }

    20% {
        opacity: 1;
    }

    35% {
        opacity: 0.3;
    }

    50% {
        opacity: 1;
        filter: brightness(1.8);
    }

    100% {
        opacity: 1;
        filter: brightness(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .led-ticker__track,
    .led-dot {
        animation: none;
    }

    .cube--on .cube-screen,
    .cube--on .cube-ring {
        animation: none;
        opacity: 1;
    }
}

/* ---------- Grid ---------- */
.articles-grid {
    max-width: 1200px;
    margin: 24px auto 0;
}

/* ---------- Mobile ---------- */
@media (max-width: 600px) {
    .news-page {
        margin-top: -24px;
    }

    .news-schedule {
        margin-top: -20px;
    }

    .cube {
        margin-top: 24px;
    }

    .cube-cables {
        top: -24px;
        height: 24px;
    }

    .cube-ring--top {
        height: 42px;
    }

    .cube-ring--bottom {
        height: 36px;
    }

    .cube-screen {
        border-left-width: 5px;
        border-right-width: 5px;
    }

    .led-ticker__logo {
        height: 26px;
    }

    .led-text {
        font-size: 0.85rem;
    }

    .lower-third {
        padding: 0 12px 12px;
    }

    .lower-third__tag {
        font-size: 0.65rem;
    }

    .lower-third__title {
        font-size: 1.15rem;
        -webkit-line-clamp: 2;
        line-clamp: 2;
    }
}
</style>
